import React, { useRef, useState } from 'react';
import {
  ChevronDown,
  Layers,
  Maximize2,
  Microscope,
  PenLine,
  Subtitles,
  Upload,
  Volume2,
  VolumeX,
} from 'lucide-react';

const trackEvent = (event: string) => {
  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible(event);
  }
};

interface VideoChapter {
  id: string;
  title: string;
  duration: string;
  blurb: string;
  src: string;
  vtt: string;
  poster?: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const CHAPTERS: VideoChapter[] = [
  {
    id: 'upload',
    title: 'Upload records',
    duration: '2:50',
    blurb:
      'Setting up a new case. Create the case, upload the bundle, frame the brief, and trigger the analysis.',
    src: '/demo/upload.mp4',
    vtt: '/demo/upload.vtt',
    Icon: Upload,
  },
  {
    id: 'chronology',
    title: 'Overview and Chronology',
    duration: '5:35',
    blurb:
      'A walk through the structured case file. Section by section on the Overview, citations into source records, the Chronology with filters, the Case Assistant, and the records themselves made navigable.',
    src: '/demo/overview-chronology.mp4',
    vtt: '/demo/overview-chronology.vtt',
    Icon: Layers,
  },
  {
    id: 'analysis',
    title: 'Standards, Evidence Integrity & Causation',
    duration: '2:30',
    blurb:
      'The three analytical tabs — what good practice looked like, what is missing or contradictory, and the chains from event to outcome. Citations carry through.',
    src: '/demo/standards-integrity-causation.mp4',
    vtt: '/demo/standards-integrity-causation.vtt',
    Icon: Microscope,
  },
  {
    id: 'drafting',
    title: 'Drafting',
    duration: '3:10',
    blurb:
      'From case file to citation-backed draft. Templates for solicitors and expert witnesses, conversational setup, generation streaming live, and the editor walkthrough.',
    src: '/demo/drafting.mp4',
    vtt: '/demo/drafting.vtt',
    Icon: PenLine,
  },
];

export const SelfGuidedTour: React.FC = () => {
  const [openChapter, setOpenChapter] = useState<string | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [mutedById, setMutedById] = useState<Record<string, boolean>>({});
  const [captionsById, setCaptionsById] = useState<Record<string, boolean>>({});

  const toggleChapter = (id: string) => {
    setOpenChapter((prev) => {
      const next = prev === id ? null : id;
      if (prev && prev !== id) {
        videoRefs.current[prev]?.pause();
      }
      if (!next) {
        videoRefs.current[id]?.pause();
      }
      return next;
    });
  };

  const handlePlay = (chapterId: string) => {
    trackEvent(`demo_video_play_${chapterId}`);
  };

  const handleFullscreen = (chapterId: string) => {
    const el = videoRefs.current[chapterId];
    if (!el) return;
    trackEvent(`demo_video_fullscreen_${chapterId}`);
    const anyEl = el as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
      webkitRequestFullscreen?: () => Promise<void>;
    };
    if (anyEl.requestFullscreen) {
      anyEl.requestFullscreen().catch(() => {});
    } else if (anyEl.webkitRequestFullscreen) {
      anyEl.webkitRequestFullscreen();
    } else if (anyEl.webkitEnterFullscreen) {
      anyEl.webkitEnterFullscreen();
    }
  };

  const handleToggleAudio = (chapterId: string) => {
    const el = videoRefs.current[chapterId];
    if (!el) return;
    const next = !el.muted;
    el.muted = next;
    setMutedById((prev) => ({ ...prev, [chapterId]: next }));
    trackEvent(`demo_video_${next ? 'mute' : 'unmute'}_${chapterId}`);
  };

  const handleToggleCaptions = (chapterId: string) => {
    const el = videoRefs.current[chapterId];
    if (!el) return;
    const tracks = el.textTracks;
    const next = !(captionsById[chapterId] ?? false);
    for (let i = 0; i < tracks.length; i += 1) {
      tracks[i].mode = next ? 'showing' : 'hidden';
    }
    setCaptionsById((prev) => ({ ...prev, [chapterId]: next }));
    trackEvent(`demo_video_captions_${next ? 'on' : 'off'}_${chapterId}`);
  };

  return (
    <>
      {/* Refine native <track> caption styling — default browser cues are oversized
          and chunky. Tightens the type, drops a soft background, gives it a bit
          of polish. */}
      <style>{`
        video::cue {
          font-size: 15px;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-weight: 500;
          line-height: 1.4;
          color: #ffffff;
          background: rgba(0, 0, 0, 0.7);
          padding: 2px 6px;
          text-shadow: none;
        }
        @media (min-width: 1024px) {
          video::cue { font-size: 16px; }
        }
      `}</style>

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-medical-900 mb-3 tracking-tight">Self-guided tour</h2>
        <p className="text-medical-600 max-w-2xl mx-auto">
          Four short videos walking through how the platform handles real medico-legal cases. Click any
          chapter to play. Subtitles available on every clip.
        </p>
      </div>

      <div className="space-y-3">
        {CHAPTERS.map((chapter) => {
          const isOpen = openChapter === chapter.id;
          return (
            <article
              key={chapter.id}
              id={chapter.id}
              className={`scroll-mt-24 rounded-xl border transition-colors bg-white ${
                isOpen ? 'border-primary-300 shadow-md' : 'border-medical-200 hover:border-primary-200'
              }`}
            >
              <button
                type="button"
                onClick={() => toggleChapter(chapter.id)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-4 px-5 py-4 text-left group"
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isOpen
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-50 text-primary-600 group-hover:bg-primary-100'
                  }`}
                >
                  <chapter.Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-base font-semibold text-medical-900">{chapter.title}</h3>
                    <span className="text-xs font-medium text-medical-400">·</span>
                    <span className="text-xs uppercase tracking-wider text-medical-500 font-medium">
                      {chapter.duration}
                    </span>
                  </div>
                  <p className="text-sm text-medical-600 leading-snug truncate">
                    {chapter.blurb}
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-medical-400 flex-shrink-0 transition-transform ${
                    isOpen ? 'rotate-180 text-primary-600' : ''
                  }`}
                />
              </button>

              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-5 pb-5">
                    <p className="text-sm text-medical-600 mb-3 leading-relaxed">{chapter.blurb}</p>
                    <div className="rounded-lg overflow-hidden shadow-md border border-medical-200/60 bg-medical-900">
                      <video
                        ref={(el) => { videoRefs.current[chapter.id] = el; }}
                        controls
                        preload="metadata"
                        playsInline
                        poster={chapter.poster}
                        onPlay={() => handlePlay(chapter.id)}
                        className="w-full h-auto block"
                      >
                        <source src={chapter.src} type="video/mp4" />
                        <track kind="captions" srcLang="en" label="English" src={chapter.vtt} />
                        Your browser does not support embedded video.
                      </video>
                    </div>
                    {/* Player controls — sound, captions, fullscreen. Captions
                        default off; users opt-in. */}
                    <div className="mt-2 flex justify-end gap-1">
                      {(() => {
                        const isMuted = mutedById[chapter.id] ?? false;
                        const captionsOn = captionsById[chapter.id] ?? false;
                        const btn = 'inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-colors';
                        const off = 'text-medical-600 hover:text-medical-900 hover:bg-medical-100';
                        const on = 'text-primary-700 hover:text-primary-900 hover:bg-primary-50';
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() => handleToggleAudio(chapter.id)}
                              aria-pressed={!isMuted}
                              className={`${btn} ${isMuted ? off : on}`}
                            >
                              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                              <span>{isMuted ? 'Sound off' : 'Sound on'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleCaptions(chapter.id)}
                              aria-pressed={captionsOn}
                              className={`${btn} ${captionsOn ? on : off}`}
                            >
                              <Subtitles className="h-4 w-4" />
                              <span>{captionsOn ? 'Captions on' : 'Captions off'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleFullscreen(chapter.id)}
                              className={`${btn} ${on}`}
                            >
                              <Maximize2 className="h-4 w-4" />
                              <span>Fullscreen</span>
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
};

export default SelfGuidedTour;
